import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Sidebar from './Sidebar';

const NEU_RAISED_LG =
  'shadow-[-10px_-10px_22px_var(--outer-shadow-light),10px_10px_22px_var(--outer-shadow-dark)]';
const NEU_RAISED_MD =
  'shadow-[-8px_-8px_18px_var(--outer-shadow-light),8px_8px_18px_var(--outer-shadow-dark)]';
const NEU_INSET_SM =
  'shadow-[inset_2px_2px_4px_var(--shadow-dark),inset_-2px_-2px_4px_var(--shadow-light)]';

const Icon = ({ name, className = '' }) => (
  <span className={`material-symbols-outlined leading-none ${className}`} aria-hidden="true">
    {name}
  </span>
);

const MyResources = () => {
  const [counts, setCounts] = useState({ notes: 0, highlights: 0, sermons: 0, devotions: 0 });
  const user = useSelector((state) => state.session.user);

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('bibleMemosTheme') || 'powder';
    } catch (_e) {
      return 'powder';
    }
  });
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'bibleMemosTheme' && event.newValue) setTheme(event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchCounts = async () => {
      try {
        const response = await fetch('/api/resource-counts');
        if (!response.ok) throw new Error('Failed to fetch resource counts');
        const data = await response.json();
        setCounts({
          notes: data.notes_count || 0,
          highlights: data.highlights_count || 0,
          sermons: data.sermons_count || 0,
          devotions: data.devotions_count || 0,
        });
      } catch (error) {
        console.error('Error fetching resource counts:', error);
      }
    };
    fetchCounts();
  }, [user]);

  const resources = [
    {
      icon: 'auto_stories',
      title: 'Devotions',
      count: counts.devotions,
      unit: 'entries',
      to: '/my-memos/devotions',
      cta: 'View Devotions',
      eyebrow: 'Reflection',
    },
    {
      icon: 'stylus_note',
      title: 'Memos',
      count: counts.notes,
      unit: 'saved notes',
      to: '/my-memos/memos',
      cta: 'View All Memos',
      eyebrow: 'Notes',
    },
    {
      icon: 'format_ink_highlighter',
      title: 'Highlights',
      count: counts.highlights,
      unit: 'highlighted verses',
      to: '/my-memos/highlights',
      cta: 'View All Highlights',
      eyebrow: 'Passages',
    },
    {
      icon: 'podium',
      title: 'Sermons',
      count: counts.sermons,
      unit: 'saved sermons',
      to: '/my-memos/sermons',
      cta: 'View All Sermons',
      eyebrow: 'Preaching',
    },
  ];

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors flex"
    >
      <Sidebar />
      <div className="flex-1 ml-[80px]">
        <div className="w-full max-w-[1440px] mx-auto px-margin-mobile md:px-margin-neu py-space-xl flex flex-col gap-space-xl">
          {/* Header card */}
          <section
            className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_LG} flex flex-col gap-space-md`}
          >
            <div className="flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                Personal Sanctuary
              </span>
              <h1 className="font-noto-serif text-headline-lg text-neu-primary">
                My Resources
              </h1>
              <p className="text-body-lg text-slate-muted max-w-2xl">
                Welcome to your personal sanctuary. Access your notes, highlights, sermons, and
                devotions — manage and explore your spiritual journey in one place.
              </p>
            </div>
          </section>

          {/* Resource cards */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-gutter">
            {resources.map((r) => (
              <div
                key={r.title}
                className={`rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} flex flex-col gap-space-md`}
              >
                <div className="flex items-start justify-between gap-space-sm">
                  <div className="w-14 h-14 rounded-full bg-accent-terracotta-soft flex items-center justify-center shadow-[inset_-2px_-2px_5px_rgba(0,0,0,0.06),inset_2px_2px_5px_rgba(255,255,255,0.7),-3px_-3px_7px_rgba(255,255,255,0.9),4px_5px_12px_rgba(0,0,0,0.18)]">
                    <Icon name={r.icon} className="text-accent-gold-deep text-[26px]" />
                  </div>
                  <span
                    className={`px-space-sm py-1 rounded-full text-label-md bg-canvas-base ${NEU_INSET_SM} text-neu-primary font-semibold`}
                  >
                    {r.count}
                  </span>
                </div>

                <div className="flex flex-col gap-space-xs">
                  <span className="text-label-sm uppercase tracking-wider text-accent-gold-deep font-semibold">
                    {r.eyebrow}
                  </span>
                  <h2 className="font-noto-serif text-headline-sm text-neu-primary">
                    {r.title}
                  </h2>
                  <p className="text-body-neu-md text-slate-muted">
                    You have <strong className="text-neu-primary">{r.count}</strong> {r.unit}.
                  </p>
                </div>

                <Link
                  to={r.to}
                  className="mt-auto inline-flex items-center justify-center gap-space-xs h-11 px-space-md rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all"
                >
                  <span>{r.cta}</span>
                  <Icon name="arrow_forward" className="text-[16px]" />
                </Link>
              </div>
            ))}
          </section>

          {/* Blessing quote card */}
          <section
            className="rounded-[2rem] p-space-lg md:p-space-xl bg-[#d4dce1] shadow-[inset_5px_5px_12px_var(--shadow-dark),inset_-5px_-5px_12px_var(--shadow-light)] flex flex-col gap-space-xs items-center text-center"
          >
            <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
              Blessing
            </span>
            <blockquote className="font-noto-serif text-headline-sm text-neu-primary italic max-w-2xl">
              “Commit to the Lord whatever you do, and he will establish your plans.”
            </blockquote>
            <cite className="text-label-md text-slate-muted not-italic">— Proverbs 16:3</cite>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MyResources;
